import { Address } from 'shared-client/types/address';
import {
	HasNoSalePrice,
	MissingDisplayPrice,
	Price,
	SimpleDisplayPrice,
} from 'shared-client/types/commerce/display-price';
import { OrderItemAddOn } from 'shared-client/types/commerce/add-ons';
import { ModelStatus } from 'shared-client/types/model-status';
import { BasicDto, PaginatedResult } from 'shared/types';
import { ShippingMethod, ShippingOption } from '../checkout/types/shipping';
// #region HCL
import {
	ProductAddOnDto,
	ProductPricingLine,
	ProductSelection,
	SimpleSelectionDto,
	Availability,
} from '../product-details/types';
// #endregion

/**
 * Represents a single line item on an order
 *
 */
export interface OrderItem {
	/**
	 *  The primary key (Guid)
	 */
	id: string;

	/**
	 * The order line item name
	 */
	name: string;

	/**
	 * The order line item Sku
	 */
	sku: string;

	/**
	 * The line item's quantity
	 */
	quantity: number;

	/**
	 *
	 * The order line item product URL
	 */
	url: string;

	/**
	 * The order line item product group URL
	 */
	groupUrl: string;

	/**
	 * The list of selectable attributes and their values for this line item
	 */
	selections?: ProductSelection[];

	// #region HCL
	/**
	 * Availability of the product
	 */
	availability?: Availability;
	// #endregion HCL
	/**
	 * The order line item related product entity
	 */
	product: OrderItemProduct;

	/**
	 * The product unit of measure for this line item
	 */
	pricingLine: ProductPricingLine;

	/**
	 * The amount of tax applied to this line item.
	 */
	tax?: SimpleDisplayPrice<Price> & HasNoSalePrice;

	/**
	 * The total discount amount applied to this line item across all quantities.
	 */
	discount?: SimpleDisplayPrice<Price> & HasNoSalePrice;

	/**
	 * The part of the order-level discount that is being spread to this line item.
	 */
	orderDiscount?: SimpleDisplayPrice<Price> & HasNoSalePrice;

	/**
	 * The displayable price of a single unit of this line item.
	 */
	unitPrice?: SimpleDisplayPrice<Price>;

	/**
	 * The displayable total price of this line item.
	 */
	totalPrice?: SimpleDisplayPrice<Price> & HasNoSalePrice;

	/**
	 * The status of the line item
	 */
	status?: OrderStatus;

	/**
	 * The list cart item attached add ons.
	 */
	addOns?: CartProductAddOnDto[];

	/**
	 * The addon order item data needed for displaying.
	 */
	orderItemAddOns?: OrderItemAddOn[];

	// #region HCL
	/**
	 * The line item's quantity ordered
	 */
	quantityShipped?: number;
	quantityOrdered?: number;
	quantityOnBackorder?: number;
	// #endregion
}

export interface CartProductAddOnDto extends ProductAddOnDto {
	totalPrice?: SimpleDisplayPrice<Price>;
}

/**
 * Represents a product related to the order line item
 */
export interface OrderItemProduct {
	/**
	 *  The primary key (Guid)
	 */
	id: string;

	/**
	 * The main image thumbnail url
	 */
	thumbnailUrl: string | null;

	/**
	 * The related brand name
	 */
	brandName: string | null;

	/**
	 * The alt text for thumbnail
	 */
	altText?: string;

	/**
	 * Status of the Product
	 */
	status?: ModelStatus;

	/**
	 * The associated group product Id of a simple product, if applicable.
	 */
	productGroupId: string;

	// #region HCL
	sku?: string;
	// #endregion
}

/**
 * Represents a single recipient on an order
 */
export interface OrderRecipient extends Address {
	/**
	 * The primary key (Guid)
	 */
	id: string;
	/**
	 * The order line items
	 */
	items: OrderItem[];
	/**
	 * The available shipping options for this recipient
	 */
	shippingOptions: ShippingOption[];
	/**
	 * The shipping service selected for this recipient
	 */
	shippingMethodId: string | null;
	/**
	 * The shipping service name selected for this recipient
	 */
	shippingMethod: ShippingMethod;
	/**
	 * The addressId of the selected address from the address book (if any)
	 */
	addressId: string | null;
	/**
	 * The order recepient's Recipient Name, used for organization orders
	 */
	recipientName?: string;

	//#region HCL
	/**
	 * The shipping account selected for this recipient
	 */
	shippingAccountId: string | null;
	isCollectShippingMethod: boolean | null;
	//#endregion
}

/**
 * Represents totals on an order
 */
export interface OrderTotals {
	/**
	 * The merchandise subtotal for the order, without consideration of discounts, tax, or shipping
	 */
	baseSubtotal: SimpleDisplayPrice<Price> & HasNoSalePrice;
	/**
	 *  The total discount amount applied to the order line items. Some discounts are applied at the order level
	 */
	lineDiscount: SimpleDisplayPrice<Price> & HasNoSalePrice;
	/**
	 * The baseSubtotal amount less the lineDiscount amount
	 */
	subtotal: SimpleDisplayPrice<Price> & HasNoSalePrice;
	/**
	 * The sum of the recipients Discount
	 */
	discount: (SimpleDisplayPrice<Price> & HasNoSalePrice) | MissingDisplayPrice;
	/**
	 * The sum of the recipients shipping discount amounts
	 */
	shippingDiscount: SimpleDisplayPrice<Price> & HasNoSalePrice;
	/**
	 * The sum of the recipients shipping amounts
	 */
	shipping?: SimpleDisplayPrice<Price> & HasNoSalePrice;
	/**
	 * The sum of the recipients tax amounts
	 */
	tax?: SimpleDisplayPrice<Price> & HasNoSalePrice;
	/**
	 * The sum of the recipients line tax amounts
	 */
	lineTax?: SimpleDisplayPrice<Price> & HasNoSalePrice;
	/**
	 * The sum of the recipients shipping tax amounts
	 */
	shippingTax?: SimpleDisplayPrice<Price> & HasNoSalePrice;
	/**
	 * The total shipping charges determined by subtracting the shipping discount shipping amount then adding the shipping tax
	 */
	shippingTotal: SimpleDisplayPrice<Price> & HasNoSalePrice;
	/**
	 * The total order charges, which is the sum of the subtotal, the shipping reduced by the shipping discount, and the tax
	 */
	total: SimpleDisplayPrice<Price> & HasNoSalePrice;
	/**
	 * The total order charges after gift cards are applied
	 */
	modifiedOrderTotal: SimpleDisplayPrice<Price> & HasNoSalePrice;
}

/**
 *  Represents a single customer order or shopping cart
 */
export interface Order extends OrderTotals, Address {
	/**
	 * The primary key (Guid)
	 */
	id: string;
	/**
	 * The ID of the organizationShipTo associated with this order.
	 * Null when the order is not created by a user in an organization, or there are no ship-tos available for the user
	 */
	organizationShipToId: string | null;
	/**
	 * The shopping cart or completed order recipients
	 */
	recipients: OrderRecipient[];
	/**
	 * The shopping cart or completed order unique cart code
	 */
	orderCode: string;
	/**
	 * The email associated to the order.
	 */
	email: string;
	/**
	 * If the billing address is the same as the shipping address of a recipient,
	 * this field will contain the ID of that recipient.
	 */
	sameAsRecipientId: string | null;

	/**
	 * The shopping cart or completed order promtions
	 */
	promotions: OrderPromotion[];

	/**
	 * The unique order number of the completed order
	 */
	orderNo: string | null;

	/**
	 * The date and time that the order was successfully placed. If null, this is still a pending shopping cart.
	 */
	processDate: Date | null;

	/**
	 * Modify date
	 */
	modifyDate: Date;

	/**
	 * The current order status
	 */
	orderStatus: OrderStatus;

	/**
	 * The localized order date string, used for display purposes
	 */
	localizedOrderDate: string;

	/**
	 * The authorization or sale payment for the current order
	 */
	paymentLogs: PaymentLog[];

	/**
	 * The gift cards saved on the order.
	 */
	paymentMethods: PaymentMethod[];

	/**
	 * If the order is locked
	 */
	isLocked: boolean;

	/**
	 * The list of notes associated to the order.
	 */
	notes: OrderNote[];

	/**
	 * The 3-character ISO 4217 code representing currency of the order
	 */
	snapshotCurrencyISO: string;

	/**
	 * The purchase order number
	 */
	purchaseOrderNumber: string;

	/**
	 * Count of all the items in the order
	 */
	totalItemsCount: number;

	/**
	 * The user specified name of saved cart.
	 * Undefined if the user has not named the cart.
	 */
	name?: string;

	// #region HCL
	exportStatus?: ExportStatus;
	trackingLink?: string;
	trackingNo?: string;
	paymentTypes: PaymentTypeEnum[];
	isP21Order: boolean;
	// #endregion
}

/**
 * Represents a single order note on an order
 */
export interface OrderNote {
	// #region HCL - nullable values
	/**
	 * The primary key (Guid)
	 */
	id: string | null;

	/**
	 * The user who created the note.
	 */
	createUser: string | null;
	// #endregion

	/**
	 * The localized date note was created used for display purpose.
	 */
	createDate: string | null;

	/**
	 * Indicates the type of note.
	 */
	noteType: string;

	/**
	 * The note entered in the admin.
	 */
	note: string;
}

/**
 * Represents a single promotion on an order
 */
export interface OrderPromotion {
	/**
	 * The primary key (Guid)
	 */
	id: string;

	/**
	 * The identifier for the promotion (Guid)
	 */
	promotionId: string;

	/**
	 * The coupon code associated with this cart or order promotion
	 */
	couponCode: string;
}

/**
 * Represents a list of user saved products
 */
export interface GetSavedProductsResult extends PaginatedResult<SavedProduct> {}

/**
 * Represents what an item's last action was in the cart
 */
export enum MoveStatus {
	MovedToCart,
	MovedToSaved,
	RemovedFromSaved,
	RemovedFromCart,
}

/**
 * Represents a single user saved product
 * Contains only the information needed to display a product's basic information and move it to the cart
 */
export interface SavedProduct {
	/**
	 * The UserSavedProduct primary key (Guid)
	 */
	id: string;

	/**
	 * The ID of the product represented by this SavedProduct
	 */
	productId: string;

	sku: string;

	name: string;

	url: string;

	groupUrl: string;

	unitPrice: SimpleDisplayPrice<Price>;

	totalPrice: SimpleDisplayPrice<Price> & HasNoSalePrice;

	selections?: SimpleSelectionDto[];

	isVirtual: boolean;

	thumbnailUrl: string | null;

	brandName: string | null;

	/**
	 * The pricing line for this line item
	 */
	pricingLine: ProductPricingLine;

	snapshotPricingLineId: string;

	snapshotUnitOfMeasureId: string;

	quantity: number;

	altText?: string;

	/**
	 * The associated group product Id of a simple product, if applicable.
	 */
	productGroupId: string;

	/**
	 * Information about add-ons for this saved product, if it has any
	 */
	addOns?: ProductAddOnDto[];
}

export interface AddToCartProps {
	/**
	 * The product id to add to cart.
	 */
	itemId: string;

	/**
	 * The quantity to be added
	 */
	quantity: number | null;

	/**
	 * The selected pricing line
	 */
	productPricingLineId: string | null;

	/**
	 * The product add ons selected
	 */
	addOns?: ProductAddOnDto[];

	/**
	 * Defines if we need to validate the product add ons before adding to cart
	 */
	validateAddOns?: boolean;
}

export interface AddAllToCartProps {
	shoppingListId: string;

	/**
	 * List of selected products ids to be added to cart
	 */
	selectedShoppingListProductIds: string[];
}

/**
 * The request used for bulk add to cart
 */
export interface BulkAddToCartProps {
	items: AddToCartProps[];
}

/**
 * Order status enum
 */
export enum OrderStatus {
	New = 'New',
	Saved = 'Saved',
	Discarded = 'Discarded',
	Ordered = 'Ordered',
	Shipped = 'Shipped',
	PartiallyShipped = 'PartiallyShipped',
	Credited = 'Credited',
	Canceled = 'Canceled',
	PartiallyCanceled = 'PartiallyCanceled',
	Returned = 'Returned',
	Completed = 'Completed',
}

/**
 * Payment Log DTO
 */
export type PaymentLog =
	| {
			paymentType: PaymentTypeEnum.CreditCard;
			last4: string;
			creditCardType: CreditCardType | null;
			amount: SimpleDisplayPrice<Price> & HasNoSalePrice;
	  }
	| {
			paymentType: PaymentTypeEnum.GiftCard;
			giftCardNumber: string;
			amount: SimpleDisplayPrice<Price> & HasNoSalePrice;
	  }
	| {
			paymentType: PaymentTypeEnum.CorporateAccount;
			amount: SimpleDisplayPrice<Price> & HasNoSalePrice;
	  };

export enum PaymentTypeEnum {
	CreditCard = 'CreditCard',
	GiftCard = 'GiftCard',
	//TODO(Paradigm): do we comment these out? Also check why 'GiftCard' wasn't here already?
	// Not implemented yet:
	ApplePay = 'ApplePay',
	GooglePay = 'GooglePay',
	PayPal = 'PayPal',
	PurchaseOrder = 'PurchaseOrder',
	CorporateAccount = 'CorporateAccount',
}

/**
 * Credit card type enum
 */
export enum CreditCardType {
	AMERICAN_EXPRESS = 'AMERICAN_EXPRESS',
	VISA = 'VISA',
	MASTERCARD = 'MASTERCARD',
	DISCOVER = 'DISCOVER',
	OTHER = 'OTHER',
}

export interface PaymentMethod extends BasicDto {
	chargeAmount: SimpleDisplayPrice<Price> & HasNoSalePrice;
	balanceAmount: SimpleDisplayPrice<Price> & HasNoSalePrice;
	paymentType: PaymentTypeEnum;
	cardNumber: string;
}

export interface AddToCartEvent {
	/**
	 * The id of the product
	 */
	id: string;
	/**
	 * The name of the product
	 */
	name: string;
	/**
	 * The product SKU
	 */
	sku: string;
	/**
	 * The price of a product
	 */
	price: number;
	/**
	 * The quantity of a product that actually got added to the cart
	 */
	adjustedQuantity: number;
	/**
	 * The 3-character ISO 4217 code representing currency of the item
	 */
	currency: string;

	/**
	 * The associated group product Id of a simple product, if applicable.
	 */
	productGroupId: string | null;

	/**
	 * The list of selectable attributes and their values for the product that was added to cart
	 */
	selections: ProductSelection[] | null;
}

/**
 * Represents the saved shopping cart
 */
export interface SavedCart {
	orderId: string;
	name: string;
	orderCode: string;
	itemCount: number;
	lastModifiedDate: string;
}

// #region HCL
export enum ExportStatus {
	Queued = 'Queued',
	Processing = 'Processing',
	Success = 'Success',
	Failed = 'Failed',
}
// #endregion
